import { Container, Grid, makeStyles } from "@material-ui/core";
import config from "../services/serviceConfig";
import React, { useState } from "react";
import { streamAppNames, streamTableNames, streamNames } from "../../util/constants";
import ButtonBar from "./ButtonBar";
import FilteredSecurities from "./FilteredSecurities";
import Header from "./Header";
import PlanWiseSecurities from "./PlanWiseSecurities";
import SecuritiesSentimentChart from "./SecuritiesSentimentChart";
import TopNSecuritiesChart from "./TopNSecuritiesChart";
import jsC8 from "jsc8";
import { parseMessage } from "../../util/helperFunctions";
import { createStreamReader, publishStreamApp, unpublishStreamApp } from "../services/streamsService";

const jsc8Client = new jsC8({
    url: config.gdnURL,
    apiKey: `${config.apiKey}`,
});

const useStyles = makeStyles({
    root: {
        padding: "0.5rem",
    },
    section: {
        paddingRight: "2rem",
    },
});

const Dashboard = () => {
    const classes = useStyles();

    const [plan, setPlan] = useState("stock");
    const [isFilteredStreamSubscribed, setIsFilteredStreamSubscribed] = useState(false);

    const [clearData, setClearData] = useState(false);
    const [isStreamStarted, setIsStreamStarted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isStopLoading, setIsStopLoading] = useState(false);
    const [isClearLoading, setIsClearLoading] = useState(false);
    const [stocksStreamData, setStocksStreamData] = useState([]);
    const [cryptoStreamData, setCryptoStreamData] = useState([]);
    const [streamConnections, setStreamConnections] = useState([]);

    const startStreamAndWebSocket = async () => {
        const response = await publishStreamApp();

        if (response) {
            const ehAlertsFreeStreamConnection = await establishConnectionForFree(streamNames[1]);
            const ehAlertsPremiumStreamConnection = await establishConnectionForPremium(streamNames[0]);

            setStreamConnections((prev) => [
                ...streamConnections,
                ehAlertsFreeStreamConnection,
                ehAlertsPremiumStreamConnection,
            ]);
            setIsStreamStarted(true);
        }
        setIsLoading(false);
    };

    const messageManipulation = (message, streamName) => {
        let tempArr = [];
        const { newData } = parseMessage(message);
        if (!newData || !newData.SummarizationItemId) {
            return;
        }

        tempArr.push(newData);
        if (streamName === streamNames[0]) {
            setStocksStreamData((prev) => [...tempArr, ...prev]);
        } else {
            setCryptoStreamData((prev) => [...tempArr, ...prev]);
        }
    };

    const establishConnectionForFree = async (streamName) => {
        try {
            const _consumer = await createStreamReader(streamName);
            _consumer.on("open", () => console.log(`Connection open for ${streamName}`));
            _consumer.on("error", (error) => console.error(`Connection error for ${streamName}`, error));
            _consumer.on("close", () => console.log(`Connection close for ${streamName}`));

            _consumer.on("message", (message) => {
                _consumer.send(JSON.stringify({ messageId: JSON.parse(message).messageId }));

                messageManipulation(message, streamNames[1]);
            });
            return _consumer;
        } catch (error) {
            console.error("error", error);
        }
    };

    const establishConnectionForPremium = async (streamName) => {
        try {
            const _consumer = await createStreamReader(streamName);
            _consumer.on("open", () => console.log(`Connection open for ${streamName}`));
            _consumer.on("error", (error) => console.error(`Connection error for ${streamName}`, error));
            _consumer.on("close", () => console.log(`Connection close for ${streamName}`));

            _consumer.on("message", (message) => {
                _consumer.send(JSON.stringify({ messageId: JSON.parse(message).messageId }));

                messageManipulation(message, streamNames[0]);
            });
            return _consumer;
        } catch (error) {
            console.error("error", error);
        }
    };

    const closeStreamAndWebSocket = async () => {
        for (const elements of streamConnections) {
            await elements.terminate();
        }

        const response = await unpublishStreamApp();
        streamAppNames.reverse();
        if (response) {
            setIsStreamStarted(false);
        }
        setIsStopLoading(false);
    };

    const clearTables = async () => {
        for (const element of streamTableNames) {
            await jsc8Client.collection(element).truncate();
        }
        setClearData(true);
        setStocksStreamData([]);
        setCryptoStreamData([]);
        setClearData(false);
        setIsClearLoading(false);
    };

    //button callback for all the rest
    const handleOnStart = () => {
        setIsLoading(true);
        startStreamAndWebSocket();
    };
    const handleOnStop = () => {
        setIsStopLoading(true);
        closeStreamAndWebSocket();
    };
    const handleOnClearTable = () => {
        setIsClearLoading(true);
        clearTables();
    };

    return (
        <Container className={classes.root} maxWidth={false}>
            <Header />
            <ButtonBar
                handleOnClearTable={handleOnClearTable}
                handleOnStart={handleOnStart}
                handleOnStop={handleOnStop}
                isStartButtonDisabled={isLoading}
                isStopButtonDisabled={isStopLoading}
                isClearButtonDisabled={isClearLoading}
                isStreamStarted={isStreamStarted}
            />
            <Grid container direction="row" alignItems="flex-start">
                <Grid item xs={8} className={classes.section}>
                    <PlanWiseSecurities
                        plan={plan}
                        setPlan={setPlan}
                        isFilteredStreamSubscribed={isFilteredStreamSubscribed}
                        cryptoStreamData={cryptoStreamData}
                        stocksStreamData={stocksStreamData}
                        isStreamAppStarted={isStreamStarted}
                    />
                    <FilteredSecurities
                        plan={plan}
                        isSubscribed={isFilteredStreamSubscribed}
                        setIsSubscribed={setIsFilteredStreamSubscribed}
                        isStreamAppStarted={isStreamStarted}
                        clearData={clearData}
                    />
                </Grid>
                <Grid item xs={4}>
                    <SecuritiesSentimentChart
                        streamData={[...stocksStreamData, ...cryptoStreamData]}
                        clearData={clearData}
                    />
                    <TopNSecuritiesChart clearData={clearData} />
                </Grid>
            </Grid>
        </Container>
    );
};

export default Dashboard;
